import { AlertCircle, RefreshCw } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";

import type { OrganizationFeatureDefinition } from "../constants/organizationsAdmin.constants";
import type { GlobalFeature, GlobalFeatureMode, GlobalFeatures } from "../types/organizationsAdmin.types";
import { FeatureRolloutCard } from "./FeatureRolloutCard";

export interface GlobalFeaturesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The features this reader may see. */
  catalog: readonly OrganizationFeatureDefinition[];
  features: GlobalFeatures | null;
  hasError: boolean;
  savingFeature: GlobalFeature | null;
  isBusy: boolean;
  /** Omitted for readers without `organizations:write`: every switch renders read-only. */
  onChange?: (feature: GlobalFeature, mode: GlobalFeatureMode) => Promise<void>;
  onRetry: () => void;
}

export function GlobalFeaturesDrawer({
  open,
  onOpenChange,
  catalog,
  features,
  hasError,
  savingFeature,
  isBusy,
  onChange,
  onRetry,
}: GlobalFeaturesDrawerProps) {
  const { t } = useTranslations("organizations");
  const globalFeatures = catalog.flatMap((definition) => (definition.global ? [definition.global] : []));

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      pinLastChild={false}
      title={t("global_settings_title")}
      description={t("global_settings_description")}
    >
      {hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {t("global_settings_load_error")}
              <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={onRetry}>
                {t("action_retry")}
              </ColocaButton>
            </span>
          }
        />
      ) : (
        <div className="space-y-3">
          {globalFeatures.map((feature) => (
            <FeatureRolloutCard
              key={feature}
              feature={feature}
              mode={features ? features[feature] : null}
              isSaving={savingFeature === feature}
              isLocked={isBusy}
              onChange={onChange ? (mode) => onChange(feature, mode) : undefined}
            />
          ))}
        </div>
      )}
    </ColocaDrawer>
  );
}
